import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import BannerFichas from '../../components/BannerFichas/BannerFichas';
import Navbar from '../../components/Navbar/Navbar';
import SessionBar from '../../components/SessionBar/SessionBar';
import Footer from '../../components/Footer/Footer';
import BotonChico03 from '../../components/Botones/BotonChico03/BotonChico03';
import { fetchUserData } from '../../utils/api';
import axiosInstance from '../../services/AxiosInstance';
import { ROUTES } from '../../const/routes';
import {
  AVATARES_DISPONIBLES,
  getAvatarSrc,
  handleAvatarError,
} from '../../utils/avatar';
import styles from './AdminUsuarios.module.css';

const USUARIOS_POR_PAGINA = 10;

const formatCurrency = (value) =>
  value.toLocaleString('es-AR', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });

const AdminUsuarios = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [userData, setUserData] = useState(null);
  const [usuarios, setUsuarios] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPaginas, setTotalPaginas] = useState(1);
  const [busqueda, setBusqueda] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [avatarCopiado, setAvatarCopiado] = useState('');
  const [copiarError, setCopiarError] = useState(false);
  const [avataresAdmin, setAvataresAdmin] = useState(AVATARES_DISPONIBLES);

  useEffect(() => {
    fetchUserData(navigate, setUserData);
  }, [navigate]);

  const fetchUsuarios = useCallback(async () => {
    setCargando(true);
    try {
      const response = await axiosInstance.get('/api/admin/usuarios', {
        params: {
          page: currentPage,
          limit: USUARIOS_POR_PAGINA,
          search: busqueda,
        },
      });
      const { data, totalPages } = response.data;
      setUsuarios(data);
      setTotalPaginas(totalPages || 1);
    } catch (error) {
      console.error('Error al obtener usuarios:', error);
      setUsuarios([]);
      setTotalPaginas(1);
    } finally {
      setCargando(false);
    }
  }, [currentPage, busqueda]);

  useEffect(() => {
    fetchUsuarios();
  }, [fetchUsuarios]);

  const fetchAvataresAdmin = useCallback(async () => {
    try {
      const response = await axiosInstance.get('/api/admin/avatares');
      const avatares = response.data?.data;
      if (Array.isArray(avatares) && avatares.length > 0) {
        setAvataresAdmin(avatares);
      }
    } catch (error) {
      // Fallback silencioso a la lista local si el endpoint no está disponible.
      setAvataresAdmin(AVATARES_DISPONIBLES);
    }
  }, []);

  useEffect(() => {
    fetchAvataresAdmin();
  }, [fetchAvataresAdmin]);

  const handleBuscar = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    setBusqueda(searchInput.trim());
  };

  const handleLimpiar = () => {
    setSearchInput('');
    setBusqueda('');
    setCurrentPage(1);
  };

  const handleCopiarNombreAvatar = async (avatar) => {
    try {
      await navigator.clipboard.writeText(avatar);
      setAvatarCopiado(avatar);
      setCopiarError(false);
      setTimeout(() => setAvatarCopiado(''), 1800);
    } catch (error) {
      setCopiarError(true);
      setTimeout(() => setCopiarError(false), 1800);
    }
  };

  return (
    <div className="flex flex-col min-h-screen">
      <BannerFichas />
      <div className="bg-gradient-to-b from-slate-950 to-violet-950 flex-grow">
        <Navbar />
        {userData && <SessionBar />}
        <div className="flex flex-col items-center py-6 md:py-10 px-2 lg:px-10">
          <h2 className="text-lg md:text-xl lg:text-2xl text-purple-400 font-bold mb-3 md:mb-4">
            {t('adminUsers.title')}
          </h2>
          <p className="text-[10px] md:text-xs lg:text-sm text-gray-300 mb-4 md:mb-6 text-center">
            {t('adminUsers.subtitle')}
          </p>

          {/* Barra de búsqueda */}
          <form
            onSubmit={handleBuscar}
            className="flex flex-col sm:flex-row gap-2 mb-6 w-full"
          >
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder={t('adminUsers.searchPlaceholder')}
              className="flex-1 px-4 py-2 rounded-lg bg-slate-800 border border-slate-600 text-white text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
            <div className="flex gap-2">
              <BotonChico03
                texto={t('adminUsers.searchButton')}
                bgColor="purple"
                funcion={() => handleBuscar({ preventDefault: () => {} })}
              />
              {busqueda && (
                <BotonChico03
                  texto={t('adminUsers.clearButton')}
                  bgColor="gray"
                  funcion={handleLimpiar}
                />
              )}
            </div>
          </form>

          <div className="w-full">
            <div
              className={`bg-bgContenedorOscuro rounded-lg px-2 md:px-5
                border-solid border-4 border-borderContenedorOscuro shadow-shadowContenedorOscuro
                shadow-[0_0_20px_rgba(100,116,139,0.9)] transition-all duration-300 ease-in-out hover:shadow-[0_0_30px_rgba(100,116,139,1)]`}
            >
              {/* Encabezado - Desktop */}
              <div className="hidden lg:block overflow-x-auto">
                <div className="min-w-[1220px]">
                  <div className="grid grid-cols-[minmax(240px,2fr)_minmax(280px,2.2fr)_minmax(160px,1.1fr)_minmax(180px,1.2fr)_minmax(180px,1.2fr)_120px] gap-4 text-purple-400 font-bold text-xs py-4 px-4 border-b-2 border-slate-700">
                    <div className="text-left">{t('adminUsers.columnPlayer')}</div>
                    <div className="text-left">{t('adminUsers.columnEmail')}</div>
                    <div className="text-center">{t('adminUsers.columnBalance')}</div>
                    <div className="text-center">{t('adminUsers.columnRole')}</div>
                    <div className="text-center">{t('adminUsers.columnStatus')}</div>
                    <div className="text-center">{t('adminUsers.columnAction')}</div>
                  </div>
                </div>
              </div>

              {/* Contenido */}
              {cargando ? (
                <div className="text-center text-gray-300 py-10 animate-pulse">
                  {t('adminUsers.loading')}
                </div>
              ) : usuarios.length === 0 ? (
                <div className="text-center text-gray-300 py-10">
                  {t('adminUsers.noUsers')}
                </div>
              ) : (
                <>
                  {/* Vista Desktop */}
                  <div className="hidden lg:block overflow-x-auto">
                    <div className="min-w-[1220px]">
                      {usuarios.map((usuario, index) => {
                        const avatarSrc = getAvatarSrc(usuario.avatar);
                        const estaDeshabilitado = !!usuario.deshabilitado;
                        const nombreCompleto =
                          [usuario.nombre_usuario, usuario.apellido_usuario]
                            .filter(Boolean)
                            .join(' ') || '-';

                        return (
                          <div
                            key={usuario.id}
                            className={`
                            grid grid-cols-[minmax(240px,2fr)_minmax(280px,2.2fr)_minmax(160px,1.1fr)_minmax(180px,1.2fr)_minmax(180px,1.2fr)_120px] gap-4 items-center py-3 px-4
                            ${estaDeshabilitado ? 'bg-red-950/30' : index % 2 === 0 ? 'bg-slate-800/60' : 'bg-slate-800/30'}
                            ${styles.adminRow}
                            text-white border-b border-slate-700/40 transition-all duration-200 hover:brightness-110
                          `}
                          >
                            <div className="flex items-center gap-3 py-1">
                              <span className="text-xs text-gray-400 font-mono w-6 text-center flex-shrink-0">
                                {usuario.id}
                              </span>
                              <img
                                src={avatarSrc}
                                alt={`Avatar de ${usuario.nick_usuario}`}
                                onError={handleAvatarError}
                                className="w-10 h-10 rounded-full border-2 border-slate-500 bg-gray-600 object-cover flex-shrink-0"
                              />
                              <div className="min-w-0">
                                <div className="font-semibold text-sm text-gray-200 whitespace-nowrap">
                                  {usuario.nick_usuario}
                                </div>
                                <div className="text-[11px] text-gray-400 whitespace-nowrap">
                                  {nombreCompleto}
                                </div>
                              </div>
                            </div>
                            <div className="text-sm text-gray-300 truncate py-1">
                              {usuario.email || '-'}
                            </div>
                            <div className="text-center text-sm font-bold text-emerald-400 tabular-nums py-1 whitespace-nowrap">
                              ${' '}
                              {formatCurrency(usuario.saldo)}
                            </div>
                            <div className="text-center">
                              <span
                                className={`inline-flex items-center justify-center whitespace-nowrap text-xs px-2 py-1 rounded-full font-semibold ${
                                  usuario.id_rol === 2
                                    ? 'bg-purple-500/20 text-purple-300 border border-purple-500/40'
                                    : 'bg-blue-500/20 text-blue-300 border border-blue-500/40'
                                }`}
                              >
                                {usuario.rol?.nombre_rol || t('adminUserDetail.noRole')}
                              </span>
                            </div>
                            <div className="text-center">
                              <span
                                className={`inline-flex items-center justify-center whitespace-nowrap text-xs px-2 py-1 rounded-full font-semibold ${
                                  estaDeshabilitado
                                    ? 'bg-red-500/20 text-red-400 border border-red-500/40'
                                    : 'bg-green-500/20 text-green-400 border border-green-500/40'
                                }`}
                              >
                                {estaDeshabilitado
                                  ? t('adminUsers.disabled')
                                  : t('adminUsers.enabled')}
                              </span>
                            </div>
                            <div className="py-1 flex justify-center items-center">
                              <BotonChico03
                                texto={t('adminUsers.viewButton')}
                                bgColor="purple"
                                funcion={() =>
                                  navigate(
                                    `${ROUTES.adminUsuarioDetalle}/${usuario.id}`
                                  )
                                }
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Vista Móvil - Tarjetas */}
                  <div className="lg:hidden py-4">
                    {usuarios.map((usuario, index) => {
                      const avatarSrc = getAvatarSrc(usuario.avatar);
                      const estaDeshabilitado = !!usuario.deshabilitado;
                      const nombreCompleto =
                        [usuario.nombre_usuario, usuario.apellido_usuario]
                          .filter(Boolean)
                          .join(' ') || '-';

                      return (
                        <div
                          key={usuario.id}
                          className={`
                            ${estaDeshabilitado ? 'bg-red-950/30' : index % 2 === 0 ? 'bg-slate-800/60' : 'bg-slate-800/30'}
                            rounded-lg p-3 mb-3 text-white
                          `}
                        >
                          <div className="flex items-center gap-3 mb-2">
                            <span className="text-xs text-gray-400 font-mono w-6 flex-shrink-0">
                              #{usuario.id}
                            </span>
                            <img
                              src={avatarSrc}
                              alt={`Avatar de ${usuario.nick_usuario}`}
                              onError={handleAvatarError}
                              className="w-9 h-9 rounded-full border-2 border-slate-500 bg-gray-600 flex-shrink-0 object-cover"
                            />
                            <div className="flex-1 min-w-0">
                              <div className="font-semibold text-xs text-gray-200">
                                {usuario.nick_usuario}
                              </div>
                              <div className="text-[10px] text-gray-400">
                                {nombreCompleto}
                              </div>
                            </div>
                            <BotonChico03
                              texto={t('adminUsers.viewButton')}
                              bgColor="purple"
                              funcion={() =>
                                navigate(
                                  `${ROUTES.adminUsuarioDetalle}/${usuario.id}`
                                )
                              }
                            />
                          </div>
                          <div className="flex flex-wrap gap-2 items-center text-[10px] ml-9 pl-0.5">
                            <span className="text-gray-400 truncate max-w-[150px]">
                              {usuario.email || '-'}
                            </span>
                            <span className="text-emerald-400 font-bold tabular-nums whitespace-nowrap">
                              ${' '}
                              {formatCurrency(usuario.saldo)}
                            </span>
                            <span
                              className={`px-1.5 py-0.5 rounded-full font-semibold ${
                                usuario.id_rol === 2
                                  ? 'bg-purple-500/20 text-purple-300 border border-purple-500/40'
                                  : 'bg-blue-500/20 text-blue-300 border border-blue-500/40'
                              }`}
                            >
                              {usuario.rol?.nombre_rol || t('adminUserDetail.noRole')}
                            </span>
                            <span
                              className={`px-1.5 py-0.5 rounded-full font-semibold ${
                                estaDeshabilitado
                                  ? 'bg-red-500/20 text-red-400 border border-red-500/40'
                                  : 'bg-green-500/20 text-green-400 border border-green-500/40'
                              }`}
                            >
                              {estaDeshabilitado
                                ? t('adminUsers.disabled')
                                : t('adminUsers.enabled')}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}

              {/* Controles de paginación */}
              <div className="flex flex-wrap justify-center items-center my-6 md:my-10 gap-2 md:gap-4">
                <BotonChico03
                  texto={t('adminUsers.previousButton')}
                  bgColor={currentPage === 1 ? 'gray' : 'pink'}
                  funcion={() => {
                    if (currentPage > 1) {
                      setCurrentPage((prev) => prev - 1);
                    }
                  }}
                />
                <span className="text-gray-300 py-1 px-3 whitespace-nowrap text-xs md:text-base">
                  {currentPage} / {totalPaginas}
                </span>
                <BotonChico03
                  texto={t('adminUsers.nextButton')}
                  bgColor={currentPage === totalPaginas ? 'gray' : 'blue'}
                  funcion={() => {
                    if (currentPage < totalPaginas) {
                      setCurrentPage((prev) => prev + 1);
                    }
                  }}
                />
              </div>
            </div>

            <div className="mt-6 md:mt-8 rounded-2xl border-4 border-slate-200 bg-slate-900 shadow-[0_0_28px_rgba(139,92,246,0.35)] overflow-hidden p-4 md:p-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4">
                <h3 className="text-sm md:text-base text-cyan-300 font-bold">
                  {t('adminUsers.avatarReference')}
                </h3>
                <span className="text-[11px] md:text-xs text-slate-300">
                  {t('adminUsers.avatarInstructions')}
                </span>
              </div>

              {(avatarCopiado || copiarError) && (
                <div className="mb-3 text-[11px] md:text-xs text-emerald-300 bg-emerald-500/10 border border-emerald-500/30 rounded px-3 py-2">
                  {copiarError
                    ? t('adminUsers.avatarCopyFailed')
                    : t('adminUsers.avatarCopied', { avatar: avatarCopiado })}
                </div>
              )}

              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
                {avataresAdmin.map((avatar) => (
                  <button
                    key={avatar}
                    type="button"
                    onClick={() => handleCopiarNombreAvatar(avatar)}
                    className="bg-slate-800/70 border border-slate-600 rounded-lg px-2 py-3 hover:border-cyan-400 hover:bg-slate-700/70 transition-colors text-center"
                    title={`Copiar ${avatar}`}
                  >
                    <img
                      src={getAvatarSrc(avatar)}
                      alt={avatar}
                      onError={handleAvatarError}
                      className="w-14 h-14 md:w-16 md:h-16 mx-auto rounded-full border-2 border-slate-500 object-cover bg-gray-600"
                    />
                    <span className="mt-2 block text-[10px] md:text-[11px] text-slate-200 font-mono break-all leading-tight">
                      {avatar}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default AdminUsuarios;
